import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { usePageSeo } from '../../utils/seo';

export default function Privacy() {
  usePageSeo({
    title: 'Политика конфиденциальности | Promokod.online',
    description: 'Политика конфиденциальности Promokod.online: порядок обработки, хранения и защиты персональных данных пользователей сервиса.',
    canonicalPath: '/privacy',
    noIndex: true,
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Политика конфиденциальности
            </h1>
            <p className="text-gray-600">
              Последнее обновление: 15 января 2025 года
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Общие положения
              </h2>
              <p className="mb-3">
                Настоящая Политика конфиденциальности определяет порядок обработки и защиты персональных данных пользователей сервиса Promokod.online (далее — «Сервис»).
              </p>
              <p className="mb-3">
                Используя Сервис, вы соглашаетесь с условиями настоящей Политики конфиденциальности. Если вы не согласны с какими-либо положениями данной Политики, пожалуйста, не используйте Сервис.
              </p>
              <p>
                Администрация Сервиса обязуется соблюдать конфиденциальность персональных данных пользователей и обеспечивать их защиту в соответствии с действующим законодательством.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Какие данные мы собираем
              </h2>
              <p className="mb-3">
                В процессе использования Сервиса мы можем собирать следующие категории персональных данных:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Регистрационные данные:</strong> имя пользователя, адрес электронной почты, пароль (в зашифрованном виде)
                </li>
                <li>
                  <strong>Технические данные:</strong> IP-адрес, тип браузера, операционная система, информация об устройстве
                </li>
                <li>
                  <strong>Данные об использовании:</strong> история просмотров, используемые промокоды, предпочтения пользователя
                </li>
                <li>
                  <strong>Контактные данные:</strong> информация, предоставленная при обращении в службу поддержки или заполнении форм обратной связи
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Цели обработки персональных данных
              </h2>
              <p className="mb-3">
                Мы используем собранные персональные данные для следующих целей:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Предоставление доступа к функционалу Сервиса</li>
                <li>Идентификация пользователя и управление учетной записью</li>
                <li>Персонализация контента и рекомендаций</li>
                <li>Улучшение качества работы Сервиса и разработка новых функций</li>
                <li>Отправка уведомлений о новых промокодах и акциях (с согласия пользователя)</li>
                <li>Обеспечение безопасности и предотвращение мошенничества</li>
                <li>Выполнение требований законодательства</li>
                <li>Связь с пользователями по вопросам работы Сервиса</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Хранение и защита данных
              </h2>
              <p className="mb-3">
                Мы принимаем все необходимые организационные и технические меры для защиты персональных данных от несанкционированного доступа, изменения, раскрытия или уничтожения:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Использование современных методов шифрования данных</li>
                <li>Ограничение доступа к персональным данным только уполномоченным сотрудникам</li>
                <li>Регулярное резервное копирование данных</li>
                <li>Мониторинг безопасности систем</li>
              </ul>
              <p className="mt-3">
                Персональные данные хранятся в течение срока, необходимого для достижения целей их обработки, или в течение срока, установленного законодательством.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Права пользователей
              </h2>
              <p className="mb-3">
                В отношении своих персональных данных вы имеете следующие права:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Право на доступ:</strong> получение информации о том, какие персональные данные о вас обрабатываются
                </li>
                <li>
                  <strong>Право на исправление:</strong> внесение изменений в неточные или неполные данные
                </li>
                <li>
                  <strong>Право на удаление:</strong> запрос на удаление ваших персональных данных
                </li>
                <li>
                  <strong>Право на ограничение обработки:</strong> ограничение обработки ваших данных в определенных случаях
                </li>
                <li>
                  <strong>Право на отзыв согласия:</strong> отзыв согласия на обработку персональных данных в любое время
                </li>
                <li>
                  <strong>Право на возражение:</strong> возражение против обработки ваших данных
                </li>
              </ul>
              <p className="mt-3">
                Для реализации своих прав обратитесь к нам по адресу: <a href="mailto:privacy@promokod.online" className="text-blue-600 hover:underline">privacy@promokod.online</a>
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Файлы cookie
              </h2>
              <p className="mb-3">
                Сервис использует файлы cookie и аналогичные технологии для улучшения работы сайта и персонализации пользовательского опыта.
              </p>
              <p className="mb-3">
                <strong>Что такое cookie?</strong> Cookie — это небольшие текстовые файлы, которые сохраняются на вашем устройстве при посещении сайта.
              </p>
              <p className="mb-3">
                <strong>Типы используемых cookie:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Необходимые cookie:</strong> обеспечивают базовую функциональность сайта
                </li>
                <li>
                  <strong>Функциональные cookie:</strong> запоминают ваши предпочтения и настройки
                </li>
                <li>
                  <strong>Аналитические cookie:</strong> помогают понять, как пользователи взаимодействуют с сайтом
                </li>
              </ul>
              <p className="mt-3">
                Вы можете управлять использованием cookie через настройки вашего браузера. Обратите внимание, что отключение cookie может ограничить функциональность Сервиса.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Передача данных третьим лицам
              </h2>
              <p className="mb-3">
                Мы не продаем и не передаем ваши персональные данные третьим лицам, за исключением следующих случаев:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>С вашего явного согласия</li>
                <li>Для выполнения требований законодательства или по запросу уполномоченных органов</li>
                <li>Поставщикам услуг, которые помогают нам в работе Сервиса (хостинг, аналитика), при условии соблюдения ими конфиденциальности</li>
                <li>В случае реорганизации, слияния или продажи бизнеса</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Изменения в Политике конфиденциальности
              </h2>
              <p className="mb-3">
                Мы оставляем за собой право вносить изменения в настоящую Политику конфиденциальности. Все изменения вступают в силу с момента их публикации на сайте.
              </p>
              <p>
                Мы рекомендуем периодически просматривать данную страницу для ознакомления с актуальной версией Политики конфиденциальности.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Контактная информация
              </h2>
              <p className="mb-3">
                Если у вас есть вопросы относительно настоящей Политики конфиденциальности или обработки ваших персональных данных, пожалуйста, свяжитесь с нами:
              </p>
              <ul className="space-y-2">
                <li>
                  <strong>Email:</strong> <a href="mailto:privacy@promokod.online" className="text-blue-600 hover:underline">privacy@promokod.online</a>
                </li>
                <li>
                  <strong>Адрес:</strong> Россия, Москва
                </li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
